<script setup lang="ts">
import { transform } from 'ol/proj'
import Map from 'ol/Map'
import View from 'ol/View'
import proj4 from 'proj4'
import { register } from 'ol/proj/proj4'
import { defineEmits, ref } from 'vue'
import Point from 'ol/geom/Point'
import { Feature } from 'ol'
import VectorLayer from 'ol/layer/Vector'
import VectorSource from 'ol/source/Vector'
import Geometry from 'ol/geom/Geometry'
import GeoJSON from 'ol/format/GeoJSON'
import BaseLayerTiles from '@/components/map/base-layers/BaseLayerTiles.vue'
import { MatchmakingLocation } from '~/types/types'

const emit = defineEmits(['mapinputchanged'])
const props = defineProps<{ locations?: MatchmakingLocation[] }>()
// eslint-disable-next-line import/no-named-as-default-member
proj4.defs(
  'EPSG:3006',
  '+proj=utm +zone=33 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs +type=crs'
)
register(proj4)

function transformCoordinates(coordinates: number[]) {
  return transform(
    coordinates.map((c) => c),
    'EPSG:4326',
    'EPSG:3857'
  )
}

function transformCoordinatesToLatLon(coordinates: number[]) {
  return transform(
    coordinates.map((c) => c),
    'EPSG:3857',
    'EPSG:4326'
  )
}

const center = ref(transformCoordinates([11, 58]))
const projection = ref('EPSG:3857')
const zoom = ref(10)
const rotation = ref(0)
const view = ref<View | null>(null)
const map = ref<{ map: Map } | null>(null)

watch(map, (newValue, oldValue) => {
  if (oldValue || !view.value) return
  const swedenExtents = [...transformCoordinates([8, 54]), ...transformCoordinates([26, 70])]
  newValue && view.value.fit(swedenExtents, { size: newValue.map.getSize() })
})

const modifyEnabled = ref(true)
const drawEnabled = ref(false)

const features = props.locations?.map(
  (l) =>
    new Feature({
      geometry: new Point(transformCoordinates([l.lng, l.lat])),
    })
)
const selectedPoints = ref<Feature<Point>[]>(features || [])

const drawend = (event: { feature: Feature<Point> }) => {
  selectedPoints.value.push(event.feature.clone())
  refreshLayer()
  modifyEnabled.value = true
  drawEnabled.value = false
  updateFeatureList()
}

const drawstart = () => {
  modifyEnabled.value = false
}

const pointsLayer = ref<{ vectorLayer: VectorLayer<VectorSource<Geometry>> } | null>(null)

const removeAllPoints = () => {
  if (!pointsLayer.value) return
  pointsLayer.value.vectorLayer.getSource()?.clear()
  selectedPoints.value = []
  updateFeatureList()
}

const coordinateList = ref<number[][]>(props.locations?.map((d) => [d.lng, d.lat]) || [])

function updateFeatureList() {
  coordinateList.value = []
  selectedPoints.value.forEach((pt) => {
    const coordinates = pt?.getGeometry()?.getCoordinates()
    coordinates && coordinateList.value.push(transformCoordinatesToLatLon(coordinates))
  })

  emit('mapinputchanged', coordinateList.value)
}
watch(pointsLayer, () => {
  refreshLayer()
})

onMounted(() => {
  refreshLayer()
})
function refreshLayer() {
  if (!pointsLayer.value) return
  pointsLayer.value.vectorLayer.changed()
  const source = pointsLayer.value.vectorLayer.getSource()

  source?.getFeatures().forEach((feature) => {
    source!.removeFeature(feature)
  })

  selectedPoints.value.forEach((feature) => {
    source?.addFeature(feature as Feature<Point>)
    feature.changed()
  })

  source?.changed()
  pointsLayer.value.vectorLayer.changed()
}
const geoJson = new GeoJSON()

function removeFeature(feature: Feature<Point>) {
  const index = selectedPoints.value.indexOf(feature)
  if (index > -1) {
    selectedPoints.value.splice(index, 1)
  }
  refreshLayer()
  updateFeatureList()
}
</script>

<template>
  <ClientOnly>
    <ol-map
      ref="map"
      :load-tiles-while-animating="true"
      :load-tiles-while-interacting="true"
      class="map"
    >
      <ol-view
        ref="view"
        :center="center"
        :rotation="rotation"
        :zoom="zoom"
        :projection="projection"
      />
      <BaseLayerTiles tile-source="lantmateriet" />
      <ol-scaleline-control />
      <ol-zoom-control />

      <ol-vector-layer ref="pointsLayer" :z-index="10">
        <ol-source-vector :format="geoJson">
          <ol-interaction-draw
            v-if="drawEnabled"
            :stop-click="true"
            type="Point"
            @drawend="drawend"
            @drawstart="drawstart"
          />
          <ol-interaction-modify v-if="modifyEnabled" @modifyend="updateFeatureList()">
          </ol-interaction-modify>
        </ol-source-vector>

        <ol-style>
          <ol-style-circle :radius="16">
            <ol-style-fill color="#eee"></ol-style-fill>
            <ol-style-stroke color="#666"></ol-style-stroke>
          </ol-style-circle>
        </ol-style>
      </ol-vector-layer>

      <ol-overlay
        v-for="(pt, index) in selectedPoints"
        :key="pt.get('ol_uid')"
        :stop-event="false"
        :position="pt.getGeometry()?.getCoordinates()"
        positioning="center-center"
      >
        <template #default>
          <div class="overlay">
            {{ index + 1 }}
            <div class="controls">
              <Button aria-label="Ta bort plats" @click="removeFeature(pt)"   size="small" rounded label="Ta bort plats"/>
            </div>
          </div>
        </template>
      </ol-overlay>
    </ol-map>

    <div class="flex justify-content-between flex-wrap py-3 gap-3">
      <div class="flex gap-2">
        <Button
          icon="pi pi-plus"
          outlined
          label="Lägg till en plats på kartan"
          @click="drawEnabled = true"
        />

        <Button label="Nollställ" outlined @click="removeAllPoints" />
      </div>
      <div>
        Totalt: <strong>{{ selectedPoints.length }}</strong> platser tillagda
      </div>
    </div>

    <!-- This has to be here, or it does not update the positions of the overlays -->
    <div v-show="false">{{ coordinateList }}</div>
  </ClientOnly>
</template>

<style scoped>
.map {
  position: relative;
  height: 300px;
  width: 100%;
  border-radius: 6px;
  overflow: hidden;
}
.overlay {
  font-family: Arial;
  color: blue;
  font-weight: 700;
  cursor: pointer;
  position: relative;
  padding: 1.5rem;
}
.controls {
  display: none;
  position: absolute;
  bottom: 0;
  transform: translate(-50%, 50%);
}
.overlay:hover .controls {
  display: block;
}
</style>
